import { useState, useEffect } from "react";
import axios from "axios";
import { GearSix, FloppyDisk, Plus, X } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Separator } from "../ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";

export function SettingsDialog({ open, onOpenChange, sessionId, API, onSuccess }) {
  const [settings, setSettings] = useState({
    app_title: "",
    dashboard_title: "",
    login_title: "",
    login_description: "",
    mannschaft_options: [],
    lehrgang_options: [],
    toast_duration: 3,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newMannschaft, setNewMannschaft] = useState("");
  const [newLehrgang, setNewLehrgang] = useState("");

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings`);
      setSettings({
        ...response.data,
        mannschaft_options: response.data.mannschaft_options || [],
        lehrgang_options: response.data.lehrgang_options || [],
      });
    } catch (err) {
      console.error("Error fetching settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/settings?session_id=${sessionId}`, settings);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error saving settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const addMannschaft = () => {
    if (newMannschaft.trim() && !settings.mannschaft_options.includes(newMannschaft.trim())) {
      setSettings((prev) => ({
        ...prev,
        mannschaft_options: [...prev.mannschaft_options, newMannschaft.trim()]
      }));
      setNewMannschaft("");
    }
  };

  const removeMannschaft = (item) => {
    setSettings((prev) => ({
      ...prev,
      mannschaft_options: prev.mannschaft_options.filter((m) => m !== item)
    }));
  };

  const addLehrgang = () => {
    if (newLehrgang.trim() && !settings.lehrgang_options.includes(newLehrgang.trim())) {
      setSettings((prev) => ({
        ...prev,
        lehrgang_options: [...prev.lehrgang_options, newLehrgang.trim()]
      }));
      setNewLehrgang("");
    }
  };

  const removeLehrgang = (item) => {
    setSettings((prev) => ({
      ...prev,
      lehrgang_options: prev.lehrgang_options.filter((l) => l !== item)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GearSix className="h-5 w-5" weight="fill" />
            App-Einstellungen
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="app_title" className="text-xs uppercase tracking-wider font-semibold">
                Sidebar-Titel
              </Label>
              <Input
                id="app_title"
                data-testid="settings-app-title"
                value={settings.app_title || ""}
                onChange={(e) => handleChange("app_title", e.target.value)}
                placeholder="z.B. Lizenzen"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dashboard_title" className="text-xs uppercase tracking-wider font-semibold">
                Dashboard-Titel
              </Label>
              <Input
                id="dashboard_title"
                data-testid="settings-dashboard-title"
                value={settings.dashboard_title || ""}
                onChange={(e) => handleChange("dashboard_title", e.target.value)}
                placeholder="z.B. Lizenz-Übersicht"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="login_title" className="text-xs uppercase tracking-wider font-semibold">
                Login-Titel
              </Label>
              <Input
                id="login_title"
                data-testid="settings-login-title"
                value={settings.login_title || ""}
                onChange={(e) => handleChange("login_title", e.target.value)}
                placeholder="z.B. Lizenz-Verwaltung"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="login_description" className="text-xs uppercase tracking-wider font-semibold">
                Login-Beschreibung
              </Label>
              <Textarea
                id="login_description"
                data-testid="settings-login-description"
                value={settings.login_description || ""}
                onChange={(e) => handleChange("login_description", e.target.value)}
                placeholder="z.B. Spieler- und Schiedsrichter-Lizenzen zentral verwalten..."
                rows={2}
              />
            </div>

            <Separator />

            {/* Mannschaft Options */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">
                Mannschaften
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {settings.mannschaft_options?.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary rounded text-xs"
                  >
                    {item}
                    <button
                      type="button"
                      onClick={() => removeMannschaft(item)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="settings-new-mannschaft"
                  value={newMannschaft}
                  onChange={(e) => setNewMannschaft(e.target.value)}
                  placeholder="Neue Mannschaft..."
                  className="h-8 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMannschaft())}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addMannschaft}
                  className="h-8 px-2"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Separator />

            {/* Lehrgang Options */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">
                Lehrgänge
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {settings.lehrgang_options?.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary rounded text-xs"
                  >
                    {item}
                    <button
                      type="button"
                      onClick={() => removeLehrgang(item)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="settings-new-lehrgang"
                  value={newLehrgang}
                  onChange={(e) => setNewLehrgang(e.target.value)}
                  placeholder="Neuer Lehrgang..."
                  className="h-8 text-sm"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLehrgang())}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLehrgang}
                  className="h-8 px-2"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Separator />

            {/* Toast Duration */}
            <div className="space-y-2">
              <Label htmlFor="toast_duration" className="text-xs uppercase tracking-wider font-semibold">
                Toast-Dauer (Sekunden)
              </Label>
              <Input
                id="toast_duration"
                data-testid="settings-toast-duration"
                type="number"
                min={1}
                max={30}
                value={settings.toast_duration || 3}
                onChange={(e) => handleChange("toast_duration", parseInt(e.target.value) || 3)}
                className="w-24 h-8 text-sm"
              />
              <p className="text-[10px] text-muted-foreground">Wie lange Toast-Benachrichtigungen angezeigt werden (1-30 Sekunden)</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Abbrechen
          </Button>
          <Button
            data-testid="settings-save-button"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Speichern...
              </>
            ) : (
              <>
                <FloppyDisk className="h-4 w-4 mr-2" />
                Speichern
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
